/**
 * Go To — what the bar's one box can reach, and what it remembers.
 *
 * Everything a person can open in ProDoc is an item: a page, an action, a
 * filtered view of the register, a document, another organisation, a FiveOX
 * screen. Items are built **from the person's verbs**, so what they may not
 * open is never listed — the palette says so in its footer rather than showing
 * a locked row.
 *
 * Matching is the kit's (`useSearch`): every typed word, anywhere in the
 * label or its keywords, in any order. Within a section, a label that starts
 * with the words ranks first.
 *
 * Recent and pinned are kept per person **and** per organisation, in this
 * browser only: one org's documents are no use in another's Go To, and the
 * next person at the desk must not see the last one's history.
 *
 * Re-keyed at W1 from the prototype's domain items (status views, tags,
 * settings sections) to the platform half's: the pages on the rail, views by
 * workspace, and the register's search. W3 retired the "not in a workspace"
 * view — every document is in one, the default workspace when nobody chose
 * (D9). **W5 puts it on real data** (FR-17): pages by verb (Tags among them),
 * documents by the register's search, **folders** and **tags** by name, the
 * workspaces — and **saved filters**, named register addresses kept beside
 * Recent and Pinned (D32), with no service table. **W22 (FR-59)**: *Upload
 * documents* opens the upload popup over the screen Go To was opened on
 * (`onUpload`) — an act, not a page, so the old *Upload* page is gone from the
 * list and the action is the one way in. **W28 (FR-65, D87)**: Settings is a
 * hub and a page per area, so the one *Document settings* item is *Settings*
 * and one item per page this person may open, each with its own words.
 */
import { mayErase, type Folder, type Tag, type Workspace } from '@fiveox/prodoc-ui'

import type { Verb } from './access'
import { DOCUMENT, SETTINGS, TAG, WORKSPACE } from './access'

export type GoToKind = 'page' | 'action' | 'view' | 'document' | 'folder' | 'tag' | 'organisation' | 'platform'

export const KIND_LABEL: Record<GoToKind, string> = {
  page: 'page',
  action: 'action',
  view: 'view',
  document: 'document',
  folder: 'folder',
  tag: 'tag',
  organisation: 'organisation',
  platform: 'FiveOX',
}

export type GoToSection = 'pinned' | 'recent' | 'saved' | 'actions' | 'pages' | 'views' | 'folders' | 'tags' | 'documents' | 'organisation'

export const SECTION_TITLE: Record<GoToSection, string> = {
  pinned: 'Pinned',
  recent: 'Recent',
  saved: 'Saved views',
  actions: 'Do',
  pages: 'Go to',
  views: 'Views of the register',
  folders: 'Folders',
  tags: 'Tags',
  documents: 'Documents',
  organisation: 'Organisation',
}

export interface GoToItem {
  /** Stable across visits — what pins and recents are remembered by. */
  id: string
  kind: GoToKind
  section: GoToSection
  label: string
  /** Quieter text after the label: "· opened 07/10/2026 08:40". */
  hint?: string
  /** Right-aligned: whose it is, or how many. */
  meta?: string
  /** Extra words it answers to, beyond its label. */
  keywords?: string
  /** A ProDoc screen. */
  to?: string
  /** Another FiveOX app. */
  href?: string
  /** Does something here instead of opening a screen (switching org). */
  run?: () => void
  /** The side panel's sentence: what opening it does. */
  about: string
  /** What this person may do there: "open", "open and change". */
  may: string
  /** For a document: when it came in. For a recent item: when it was opened. */
  when?: string
  /** Settled by `run`, so it cannot be remembered and re-run later. */
  pinnable: boolean
}

/** A folder as Go To lists it: the folder, and the workspace it is in. */
export interface FolderRef {
  folder: Folder
  workspace: Pick<Workspace, 'id' | 'name'>
}

/** What the palette needs to know to build the list. Anything not loaded yet
 *  is null; its items simply appear once it arrives. */
export interface GoToContext {
  can: (resource: string, verb: Verb) => boolean
  otherOrgs: { id: string; name: string }[]
  workspaces: Workspace[] | null
  /** W5: the organisation's folders the reader sees, read when the palette is first searched. */
  folders?: FolderRef[] | null
  /** W5: the organisation's tags. */
  tags?: Tag[] | null
  /** W5: this person's saved filters in this organisation (D32). */
  saved?: SavedFilter[]
  consoleUrl: string
  accountsUrl: string
  onSwitchOrg: (orgId: string) => void
  /** W22 (FR-59): opens the upload popup. Without it, the action opens Documents with the popup open. */
  onUpload?: () => void
}

const count = (n: number | undefined, one: string, many = `${one}s`) =>
  n === undefined ? undefined : `${n} ${n === 1 ? one : many}`

/**
 * W28 (FR-65, D87): each settings page as Go To offers it — the hub's cards by
 * another door, each with the words a person would look for it by. `only`
 * narrows who is offered one: the month's usage is the administrator's (engine
 * C-E11), erasure is `mayErase`'s (D25).
 */
const SETTINGS_PAGES: { key: string; label: string; keywords: string; about: string; may?: string; only?: (can: GoToContext['can']) => boolean }[] = [
  {
    key: 'types',
    label: 'Document types',
    keywords: 'types kinds invoice statement label rename code in use switch off add own',
    about: 'What a person may call a document: the shipped types in use, and the organisation’s own codes.',
  },
  {
    key: 'review',
    label: 'Review',
    keywords: 'review on upload approve approval four eyes two people own upload uploader defaults',
    about: 'Which uploads wait for a reviewer, and whether the uploader may approve their own (four-eyes).',
  },
  {
    key: 'retention',
    label: 'Retention',
    keywords: 'retention keep kept years months floor statute law minimum rule expire',
    about: 'How long each type is kept: the law’s minimum, with its citation, and the organisation’s rules above it.',
  },
  {
    key: 'sharing',
    label: 'Sharing',
    keywords: 'sharing share link expiry days passcode send email whatsapp channel',
    about: 'How long a new link works, whether it needs a passcode, and how a sent link leaves the organisation.',
  },
  {
    key: 'parsing',
    label: 'Parsing',
    keywords: 'parsing parse extraction extract read ai gemini india automatic acceptance accept reconcile',
    about: 'Whether ProDoc’s screens offer to read a document, which types it reads, whether a reading that reconciles is accepted on its own, and where AI may run.',
  },
  {
    key: 'usage',
    label: 'Parsing usage',
    keywords: 'usage parsing cost month model calls ai rupees completed refused',
    about: 'What ProDoc read in a month and what the AI it needed cost, by document type.',
    may: 'open',
    only: (can) => can(SETTINGS, 'can_update'),
  },
  {
    key: 'erasure',
    label: 'Personal-data erasure',
    keywords: 'erasure erase dpdp personal data privacy request principal',
    about: 'A person’s request to erase their documents, recorded behind re-verification; what the law still requires is held until its date.',
    may: 'open and record a request',
    only: mayErase,
  },
]

/** Everything reachable that is not a document — the fixed part of the list. */
export function staticItems(ctx: GoToContext): GoToItem[] {
  const { can } = ctx
  const items: GoToItem[] = []

  /* ------------------------------------------------------------ do */
  if (can(DOCUMENT, 'can_create')) {
    items.push({
      id: 'action:upload',
      kind: 'action',
      section: 'actions',
      label: 'Upload documents',
      hint: '· checked, hashed and stored',
      keywords: 'upload add new file files batch scan pdf excel csv image photo create import drop',
      // The popup, over whatever screen Go To was opened on (FR-59); revived from Pinned as the live item, so it keeps its act.
      ...(ctx.onUpload ? { run: ctx.onUpload } : { to: '/documents?upload=1' }),
      about:
        'Opens the upload over this screen: drop or choose files — as many as you like. Each is checked, hashed and stored in this organisation’s private store, filed in a workspace. Closing it does not stop the files.',
      may: 'upload',
      pinnable: true,
    })
  }
  if (can(WORKSPACE, 'can_create')) {
    items.push({
      id: 'action:workspace',
      kind: 'action',
      section: 'actions',
      label: 'Add a workspace',
      keywords: 'new workspace team finance purchase stores create',
      to: '/admin/workspaces',
      about: 'Opens Workspaces, where a new one is added and the people who see it are named.',
      may: 'add',
      pinnable: true,
    })
  }

  /* --------------------------------------------------------- pages */
  items.push({
    id: 'page:home',
    kind: 'page',
    section: 'pages',
    label: 'Overview',
    keywords: 'dashboard home summary start counts',
    to: '/',
    about: 'Headline counts, documents by workspace and the latest uploads.',
    may: 'open',
    pinnable: true,
  })
  if (can(DOCUMENT, 'can_view')) {
    items.push({
      id: 'page:documents',
      kind: 'page',
      section: 'pages',
      label: 'Documents',
      keywords: 'register list all search find filter export excel',
      to: '/documents',
      about: 'The register: every document you can see, newest first — searchable by name, with filters by workspace, owner app and upload date, and export.',
      may: 'open',
      pinnable: true,
    })
  }
  if (can(DOCUMENT, 'can_update')) {
    items.push({
      id: 'page:review',
      kind: 'page',
      section: 'pages',
      label: 'Review',
      keywords: 'review approve reject queue waiting four eyes decision check',
      to: '/review',
      about: 'Documents waiting for a decision, oldest first — each beside its file, approved or rejected with a comment the uploader reads.',
      may: 'decide',
      pinnable: true,
    })
  }
  if (can(TAG, 'can_update')) {
    items.push({
      id: 'page:admin-tags',
      kind: 'page',
      section: 'pages',
      label: 'Tags',
      hint: '· Admin',
      keywords: 'tags labels rename recolour colour merge delete manage admin',
      to: '/admin/tags',
      about: 'The organisation’s tags with how many documents carry each — rename, recolour, merge or delete one.',
      may: 'open and change',
      pinnable: true,
    })
  }
  if (can(WORKSPACE, 'can_create')) {
    items.push({
      id: 'page:admin-workspaces',
      kind: 'page',
      section: 'pages',
      label: 'Workspaces',
      hint: '· Admin',
      keywords: 'workspaces team finance purchase stores who sees members granted admin',
      to: '/admin/workspaces',
      about: 'Workspaces, who sees each, and how many documents each holds.',
      may: 'open and change',
      pinnable: true,
    })
  }

  if (can(SETTINGS, 'can_view')) {
    // W28 (FR-65, D87): the hub, then one item per page this person may open, each answering to its own words.
    const admin = can(SETTINGS, 'can_update')
    items.push({
      id: 'page:settings',
      kind: 'page',
      section: 'pages',
      label: 'Settings',
      keywords: 'document settings preferences configuration options organisation console',
      to: '/settings',
      about: 'ProDoc’s settings for this organisation, a card per area with what is set now — and the FiveOX console for the organisation, its people and its messaging.',
      may: 'open',
      pinnable: true,
    })
    for (const page of SETTINGS_PAGES) {
      if (page.only && !page.only(can)) continue
      items.push({
        id: `page:settings-${page.key}`,
        kind: 'page',
        section: 'pages',
        label: page.label,
        hint: '· Settings',
        keywords: page.keywords,
        to: `/settings/${page.key}`,
        about: page.about,
        may: page.may ?? (admin ? 'open and change' : 'open'),
        pinnable: true,
      })
    }
  }

  /* --------------------------------------------------------- views */
  if (can(DOCUMENT, 'can_view')) {
    const views: { id: string; label: string; to: string; n?: number; keywords: string; about: string }[] = []
    for (const w of ctx.workspaces ?? []) {
      views.push({
        id: `workspace_${w.id}`,
        label: w.is_default ? `${w.name} (default)` : w.name,
        to: `/documents?workspace=${encodeURIComponent(w.id)}`,
        n: w.document_count,
        keywords: `${w.description ?? ''} workspace${w.is_default ? ' default unfiled everyone' : ''}`,
        about: w.is_default
          ? `Every document filed in ${w.name} — the default workspace, where an upload lands when nobody chose one. Every member sees it.`
          : `Every document filed in ${w.name}.`,
      })
    }
    for (const v of views) {
      items.push({
        id: `view:${v.id}`,
        kind: 'view',
        section: 'views',
        label: v.label,
        meta: count(v.n, 'document'),
        keywords: `${v.keywords} documents register filter`,
        to: v.to,
        about: v.about,
        may: 'open',
        pinnable: true,
      })
    }
  }

  /* ------------------------------------------------ saved filters */
  if (can(DOCUMENT, 'can_view')) {
    for (const f of ctx.saved ?? []) {
      items.push({
        id: f.id,
        kind: 'view',
        section: 'saved',
        label: f.name,
        hint: '· saved view',
        keywords: 'saved view filter register documents',
        to: f.to,
        about: 'A view of the register you saved — its filters, folder and sort — kept for you in this organisation, in this browser.',
        may: 'open',
        when: `saved ${f.at.slice(0, 10)}`,
        // Already kept; a saved view is forgotten from its own panel, not unpinned.
        pinnable: false,
      })
    }
  }

  /* ------------------------------------------------ folders, tags */
  if (can(DOCUMENT, 'can_view')) {
    for (const { folder, workspace } of ctx.folders ?? []) {
      if (folder.status === 'archived') continue
      items.push({
        id: `folder:${folder.id}`,
        kind: 'folder',
        section: 'folders',
        label: folder.name,
        hint: `· ${workspace.name}`,
        meta: folder.owner_app !== 'prodoc' ? folder.owner_app : undefined,
        keywords: `folder ${workspace.name} ${folder.owner_app}`,
        to: `/documents?workspace=${encodeURIComponent(workspace.id)}&folder=${encodeURIComponent(folder.id)}`,
        about: `Every document filed in “${folder.name}”, in ${workspace.name}. The register’s tree browses into its folders from there.`,
        may: 'open',
        pinnable: true,
      })
    }
    for (const tag of ctx.tags ?? []) {
      items.push({
        id: `tag:${tag.id}`,
        kind: 'tag',
        section: 'tags',
        label: tag.name,
        meta: count(tag.document_count, 'document'),
        keywords: 'tag label tagged',
        to: `/documents?tag=${encodeURIComponent(tag.id)}`,
        about: `Every document tagged “${tag.name}”, whichever workspace or folder it is in.`,
        may: can(TAG, 'can_update') ? 'open; manage it under Tags' : 'open',
        pinnable: true,
      })
    }
  }

  /* -------------------------------------------------- organisation */
  for (const org of ctx.otherOrgs) {
    items.push({
      id: `org:${org.id}`,
      kind: 'organisation',
      section: 'organisation',
      label: `Switch to ${org.name}`,
      keywords: 'organisation org company switch change tenant',
      run: () => ctx.onSwitchOrg(org.id),
      about: `Work in ${org.name} instead. Every screen reloads with its documents.`,
      may: 'switch',
      pinnable: false,
    })
  }
  items.push({
    id: 'platform:console',
    kind: 'platform',
    section: 'organisation',
    label: 'Members and roles',
    hint: '· FiveOX console',
    keywords: 'members people users invite roles access permissions admin uploader reviewer viewer',
    href: `${ctx.consoleUrl}/access`,
    about: 'Who is in the organisation and which ProDoc role each holds. This is a FiveOX screen.',
    may: 'open',
    pinnable: true,
  })
  items.push({
    id: 'platform:account',
    kind: 'platform',
    section: 'organisation',
    label: 'Your account and security',
    hint: '· FiveOX Accounts',
    keywords: 'profile password two step mfa security sign in devices',
    href: ctx.accountsUrl,
    about: 'Your name, password, two-step sign-in and trusted devices. This is a FiveOX screen.',
    may: 'open',
    pinnable: true,
  })

  return items
}

/* --------------------------------------------------------- matching */

const haystack = (item: GoToItem) => `${item.label} ${item.hint ?? ''} ${item.keywords ?? ''} ${KIND_LABEL[item.kind]}`.toLowerCase()

/** Every word, anywhere, any order — the kit's `useSearch` rule. */
export function matches(item: GoToItem, query: string): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return true
  const text = haystack(item)
  return terms.every((t) => text.includes(t))
}

/** 0 when the label starts with the query, 1 when a label word does, 2 otherwise. */
export function rank(item: GoToItem, query: string): number {
  const q = query.trim().toLowerCase()
  const label = item.label.toLowerCase()
  if (!q) return 0
  if (label.startsWith(q)) return 0
  if (label.split(/[\s“”"(&·-]+/).some((w) => w.startsWith(q.split(/\s+/)[0]!))) return 1
  return 2
}

/* ---------------------------------------------------------- history */

/** What is kept of an item: enough to list it and open it again. */
export interface Remembered {
  id: string
  kind: GoToKind
  label: string
  to?: string
  href?: string
  meta?: string
  about?: string
  /** ISO time it was opened (recent only). */
  at?: string
}

/** A saved filter (FR-17, D32): a register address with a name — no service table. */
export interface SavedFilter {
  id: string
  name: string
  /** `/documents?…` — the register's own address for the view. */
  to: string
  /** ISO time it was saved. */
  at: string
}

interface History {
  recent: Remembered[]
  pins: Remembered[]
  saved: SavedFilter[]
}

const RECENT_MAX = 6
/** A per-person convenience, not a session key — so it keeps the prototype's name (prodoc-ui.md §6-18). */
const keyFor = (userId: string, orgId: string) => `prodoc.goto.${userId}.${orgId}`

export function readHistory(userId: string | null | undefined, orgId: string | undefined): History {
  if (!userId || !orgId) return { recent: [], pins: [], saved: [] }
  try {
    const raw = localStorage.getItem(keyFor(userId, orgId))
    const parsed = raw ? (JSON.parse(raw) as Partial<History>) : {}
    return {
      recent: Array.isArray(parsed.recent) ? parsed.recent : [],
      pins: Array.isArray(parsed.pins) ? parsed.pins : [],
      // Only a register address is a saved filter: anything else in the store is not followed.
      saved: Array.isArray(parsed.saved) ? parsed.saved.filter((f) => typeof f?.to === 'string' && f.to.startsWith('/documents') && typeof f.name === 'string') : [],
    }
  } catch {
    return { recent: [], pins: [], saved: [] }
  }
}

function writeHistory(userId: string, orgId: string, history: History) {
  try {
    localStorage.setItem(keyFor(userId, orgId), JSON.stringify(history))
  } catch {
    /* private window or full storage: Go To still works, it just forgets */
  }
}

export const remember = (item: GoToItem): Remembered => ({
  id: item.id,
  kind: item.kind,
  label: item.label,
  to: item.to,
  href: item.href,
  meta: item.kind === 'document' ? item.meta : undefined,
  about: item.about,
})

/** Put an opened item at the front of Recent. */
export function recordVisit(userId: string | null | undefined, orgId: string | undefined, entry: Remembered) {
  if (!userId || !orgId) return
  const history = readHistory(userId, orgId)
  const recent = [{ ...entry, at: new Date().toISOString() }, ...history.recent.filter((r) => r.id !== entry.id)].slice(0, RECENT_MAX)
  writeHistory(userId, orgId, { ...history, recent })
}

/** Pin or unpin. Returns whether it is pinned now. */
export function togglePin(userId: string | null | undefined, orgId: string | undefined, entry: Remembered): boolean {
  if (!userId || !orgId) return false
  const history = readHistory(userId, orgId)
  const pinned = history.pins.some((p) => p.id === entry.id)
  const pins = pinned ? history.pins.filter((p) => p.id !== entry.id) : [...history.pins, entry]
  writeHistory(userId, orgId, { ...history, pins })
  return !pinned
}

const SAVED_MAX = 30

/**
 * Save the register's current address under a name (FR-17, D32), per person
 * and organisation in this browser. A second view of the same name replaces
 * the first. Returns the saved filter, or `null` when there is nobody to keep
 * it for.
 */
export function saveFilter(userId: string | null | undefined, orgId: string | undefined, input: { name: string; to: string }): SavedFilter | null {
  if (!userId || !orgId || !input.to.startsWith('/documents')) return null
  const name = input.name.trim().slice(0, 80)
  if (!name) return null
  const history = readHistory(userId, orgId)
  const entry: SavedFilter = { id: `saved:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, name, to: input.to, at: new Date().toISOString() }
  const saved = [entry, ...history.saved.filter((f) => f.name.toLowerCase() !== name.toLowerCase())].slice(0, SAVED_MAX)
  writeHistory(userId, orgId, { ...history, saved })
  return entry
}

/** Forget a saved filter. */
export function removeFilter(userId: string | null | undefined, orgId: string | undefined, id: string): void {
  if (!userId || !orgId) return
  const history = readHistory(userId, orgId)
  writeHistory(userId, orgId, { ...history, saved: history.saved.filter((f) => f.id !== id) })
}

/** The item a screen address belongs to, for Recent: an exact view first, then
 *  the page. Documents record themselves (the page knows the name). */
export function itemForPath(items: GoToItem[], pathname: string, search: string, hash: string): GoToItem | undefined {
  const full = `${pathname}${search}${hash}`
  return (
    items.find((i) => i.to === full && i.kind !== 'action') ??
    items.find((i) => i.to === `${pathname}${search}` && i.kind !== 'action') ??
    items.find((i) => i.kind === 'page' && i.to === pathname)
  )
}
